import { useState } from 'react';

import { cx } from '@mage-ui/styled-system/css';
import {
  clearSection,
  comboboxOption,
  input,
  inputLabel,
  inputRoot,
  inputSection,
  selectButton,
  selectSection,
} from '@mage-ui/styled-system/recipes';
import { InputText, type InputTextProps } from '@/components/controls';

import { DropdownBase, type DropdownBaseProps } from './DropdownBase';

export type SelectProps = Omit<
  DropdownBaseProps,
  'children' | 'onClick' | 'setQuery' | 'target'
> &
  Omit<InputTextProps, 'isClearable' | 'clearableIcon'> & {
    setSelectedOption: (
      option: { id: string | number; value: string } | undefined,
    ) => void;
    options: { id: string | number; value: string }[];
    isClearable?: boolean;
    clearableIcon?: string;
    classNames?: {
      option?: string;
    };
  };

export const Select = ({
  defaultOpened = false,
  setSelectedOption,
  selectedOption,
  options,
  classNames,
  isClearable,
  clearableIcon,
  ...props
}: SelectProps) => {
  const [query, setQuery] = useState(selectedOption?.value ?? '');

  const dropdownOptions = options.map((option) => (
    <DropdownBase.Option
      value={option.value}
      key={option.id}
      active={selectedOption?.id === option.id}
      classNames={{ option: classNames?.option ?? comboboxOption() }}
    >
      {option.value}
    </DropdownBase.Option>
  ));

  const onChange = (value: string | undefined) => {
    const selected = options.find((item) => item.value === value);
    setSelectedOption(selected);
  };

  return (
    <DropdownBase
      readOnly={true}
      target={
        <InputText
          readOnly
          classNames={{
            root: classNames?.root ?? inputRoot(),
            label: classNames?.label ?? inputLabel(),
            input: classNames?.input ?? cx(input(), selectButton()),
            section:
              classNames?.section ??
              cx(
                inputSection(),
                isClearable && query ? clearSection() : selectSection(),
              ),
          }}
          value={query}
          {...props}
        />
      }
      selectedOption={selectedOption}
      setQuery={setQuery}
      setSelectedOption={(option) => onChange(option)}
      width='target'
      isClearable={isClearable}
      clearableIcon={clearableIcon}
      {...props}
    >
      {dropdownOptions}
    </DropdownBase>
  );
};
